import { API_BASE_URL, API_BASE_URL_V2 } from '../config/api';

class ApiService {
  getAuthHeaders() {
    const token = localStorage.getItem('mgm_token');
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async fetchWithErrorHandling(url, options = {}) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      if (response.status === 401) {
        // Token expired or invalid - clear and redirect to login
        localStorage.removeItem('mgm_token');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // ==================== V2 AUTH APIs ====================

  async login(email, password) {
    const response = await fetch(`${API_BASE_URL_V2}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    return data.data;
  }

  async getCurrentUser() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/auth/me`);
    return data.data;
  }

  async changePassword(currentPassword, newPassword) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    return data;
  }

  // ==================== V2 ORDERS APIs ====================

  async getOrdersV2() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/mgmorders`);
    return data.data || [];
  }

  async getActiveOrdersV2() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/mgmactiveorders`);
    return data.data || [];
  }

  async getOrderStatsV2() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/mgmorders/stats`);
    return data.data || {};
  }

  // ==================== V2 SUBSCRIPTIONS APIs ====================

  async getSubscriptionsV2() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/subscription`);
    return data.data || [];
  }

  async createSubscriptionV2(subscriptionData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/subscription`, {
      method: 'POST',
      body: JSON.stringify(subscriptionData),
    });
    return data.data;
  }

  async deleteSubscriptionV2(id) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/subscription/${id}`, {
      method: 'DELETE',
    });
    return data.data;
  }

  // ==================== V2 USER/CATALOG APIs ====================

  async getUserCatalogs() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/user/catalogs`);
    return data.data || [];
  }

  async getUsersV2() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/users`);
    return data.data || [];
  }

  async updateUserV2(userId, userData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return data.data;
  }

  // ==================== V2 MANUAL ORDER APIs ====================

  async getManualOrderCatalogs() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/manual-order/catalogs`);
    return data.data || [];
  }

  async getManualOrderUsers(catalogId) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/manual-order/users?catalog_id=${catalogId}`);
    return data.data || [];
  }

  async placeManualOrder(orderData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/manual-order`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    return data;
  }

  // ==================== V2 BULK ORDER MANAGEMENT APIs ====================

  async bulkExitOrders(orderIds) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/orders/bulk-exit`, {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds }),
    });
    return data.data;
  }

  async bulkReactivateOrders(orderIds) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/orders/bulk-reactivate`, {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds }),
    });
    return data.data;
  }

  async bulkCancelOrders(orderIds) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/orders/bulk-cancel`, {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds }),
    });
    return data.data;
  }

  // ==================== V1 APIs (Legacy - kept for compatibility) ====================

  // Orders
  async getOrders() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/mgmorders`);
    return data.data || [];
  }

  async getActiveOrders() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/mgmactiveorders`);
    return data.data || [];
  }

  // Alerts
  async getAlerts() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/alert`);
    return data.data || [];
  }

  async getAlert(id) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/alert/${id}`);
    return data.data;
  }

  // Catalogs
  async getCatalogs() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/catalog`);
    return data.data || [];
  }

  async createCatalog(catalogData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/catalog`, {
      method: 'POST',
      body: JSON.stringify(catalogData),
    });
    return data.data;
  }

  // Strategies
  async getStrategies() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/Strategy`);
    return data.data || [];
  }

  async createStrategy(strategyData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/Strategy`, {
      method: 'POST',
      body: JSON.stringify(strategyData),
    });
    return data.data;
  }

  // Baskets (Order Templates)
  async getBaskets() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/Basket`);
    return data.data || [];
  }

  async getBasket(id) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/Basket/${id}`);
    return data.data;
  }

  // Users
  async getUsers() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/user`);
    return data.data || [];
  }

  async getUser(id) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/user/${id}`);
    return data.data;
  }

  async createUser(userData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/user`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return data.data;
  }

  // Credentials (V2 - role-based)
  async getCredentials() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/credentials`);
    return data.data || [];
  }

  async getDpUserIds() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/credential/dp_user_id`);
    return data.data || [];
  }

  async generateToken(dpUserId) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/credentials/generate-token`, {
      method: 'POST',
      body: JSON.stringify({ dp_user_id: dpUserId }),
    });
    return data.data;
  }

  async getTokenStatus(dpUserId) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL_V2}/credentials/token-status/${dpUserId}`);
    return data.data;
  }

  async createCredential(credentialData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/credential`, {
      method: 'POST',
      body: JSON.stringify(credentialData),
    });
    return data.data;
  }

  // Catalog Trades (Performance)
  async getCatalogTrades() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/catalog/trade`);
    return data.data || [];
  }

  async createCatalogTrade(tradeData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/catalog/trade`, {
      method: 'POST',
      body: JSON.stringify(tradeData),
    });
    return data.data;
  }

  // Subscriptions (V1 - kept for compatibility)
  async getSubscriptions() {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/subscription`);
    return data.data || [];
  }

  async createSubscription(subscriptionData) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/subscription`, {
      method: 'POST',
      body: JSON.stringify(subscriptionData),
    });
    return data.data;
  }

  async deleteSubscription(id) {
    const data = await this.fetchWithErrorHandling(`${API_BASE_URL}/subscription/${id}`, {
      method: 'DELETE',
    });
    return data.data;
  }
}

export const apiService = new ApiService();
export default apiService;
